import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../helpers';
import { DEFAULT_ICON_CLASS_NAME } from '../../theme/constants/icon';

const SummaryDataItem = ({ title,
  titleVariant, titleColor, fontWeight, valueVariant, valueColor,
  value, expandable, children, loading, isMoney, discount, isChild }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const iconAnimationStyles = {
    ml: 1,
    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.3s ease-in-out',
  };
  const actualValueVariant = valueVariant || (isChild ? 'small_2' : 'small_3');
  const actualFontWeight = fontWeight || (isChild ? 500 : 600);

  const getFormattedValue = () => {
    let valueToShow = value;
    if (isMoney) {
      valueToShow = formatMoney(value, true);
    }
    if (valueToShow === '0' || valueToShow === '0%') {
      valueToShow = '-';
    }
    const discountText = discount && valueToShow !== '-' ? t('discount') : '';
    return `${discountText}${valueToShow}`;
  };

  const valueContent = loading || !value ? (
    <Skeleton variant="text" width={80} height={24} sx={{ display: 'inline-block' }} />
  ) : (
    <Typography
      variant={actualValueVariant}
      fontWeight={actualFontWeight}
      color={valueColor}
    >
      {getFormattedValue()}
    </Typography>
  );

  if (expandable) {
    return (
      <Accordion
        variant="gray"
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        sx={{
          boxShadow: 'none',
          background: 'transparent',
          width: '100%',
        }}
      >
        <AccordionSummary
          sx={{ px: 0 }}
        >
          <Grid container alignItems="center">
            <Grid item xs={7} md={7} display="flex" alignItems="center">
              <Typography variant={titleVariant} fontWeight={actualFontWeight} color={titleColor}>
                {title}
              </Typography>
              <ExpandMoreIcon
                className={DEFAULT_ICON_CLASS_NAME}
                sx={iconAnimationStyles}
              />
            </Grid>
            <Grid item xs={5} md={5} textAlign="right">
              {valueContent}
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Divider sx={{ borderColor: 'gray.A300', borderWidth: '1px', borderRadius: '10px' }} />
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, py: 0, my: 1 }}>
          <Grid container rowSpacing={1}>
            {children}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  }
  return (
    <Grid container alignItems="center">
      <Grid item xs={7} md={7}>
        <Typography variant={titleVariant} fontWeight={actualFontWeight} color={titleColor}>
          {title}
        </Typography>
      </Grid>
      <Grid item xs={5} md={5} textAlign="right">
        {valueContent}
      </Grid>
    </Grid>
  );
};

SummaryDataItem.propTypes = {
  title: PropTypes.string.isRequired,
  titleVariant: PropTypes.string,
  titleColor: PropTypes.string,
  valueVariant: PropTypes.string,
  fontWeight: PropTypes.number,
  valueColor: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({
    }),
  ]),
  expandable: PropTypes.bool,
  children: PropTypes.node,
  loading: PropTypes.bool,
  isMoney: PropTypes.bool,
  discount: PropTypes.bool,
  isChild: PropTypes.bool,
};

SummaryDataItem.defaultProps = {
  expandable: false,
  children: null,
  loading: false,
  titleVariant: 'small_2',
  titleColor: 'gray.A600',
  fontWeight: null,
  valueVariant: null,
  valueColor: 'gray.A600',
  isMoney: false,
  discount: false,
  isChild: false,
  value: undefined,
};

export default SummaryDataItem;
